import React, { useEffect } from 'react';

import { isSSR } from '@atlaskit/editor-common/core-utils';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { PMPlugin, SelectionToolbarGroup } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import type { AIDefinitionsPlugin } from './aiDefinitionsPluginType';
import { updateState } from './editor-actions/update-state';
import { onLivePageModeChange, openReadingAidsPopup } from './pm-plugins/ai-definitions/commands';
import { createAIDefinitionsPMPlugin } from './pm-plugins/ai-definitions/plugin';
import { getPluginState } from './pm-plugins/ai-definitions/plugin-factory';
import { aiDefinitionsPluginKey } from './pm-plugins/ai-definitions/plugin-key';
import { getAIDefineButton } from './pm-plugins/selection-toolbar-buttons/define';
import type { EditorViewRef } from './types';
import { AIDefinitions } from './ui/AIDefinitions';
import { AIDefinitionsErrorBoundary } from './ui/AIDefinitionsErrorBoundary';
import {
	getReadingAidsSelectionProps,
	selectionExceedsMaxWordCount,
	selectionHasText,
} from './ui/utils/selection';

export const aiDefinitionsPlugin: AIDefinitionsPlugin = ({ api, config }) => {
	const editorViewRef: EditorViewRef = { current: undefined };

	return {
		name: 'aiDefinitions',

		usePluginHook: ({ editorView }) => {
			const enabled = useSharedPluginStateSelector(api, 'aiDefinitions.enabled');
			const autoHighlightEnabled = useSharedPluginStateSelector(
				api,
				'aiDefinitions.autoHighlightEnabled',
			);
			const livePageMode = useSharedPluginStateSelector(api, 'editorViewMode.mode');

			useEffect(() => {
				if (!enabled) {
					return;
				}
				// pass autoHighlightEnabled here, so that
				// when command updateState is called, it will trigger usePluginHook to rerun
				return onLivePageModeChange({
					api,
					config,
					editorView,
					livePageMode,
					autoHighlightEnabled,
				});
			}, [editorView, enabled, autoHighlightEnabled, livePageMode]);
		},

		pmPlugins() {
			const pmPlugins: PMPlugin[] = [
				{
					name: 'aiDefinitionsEditorViewRef',
					plugin: () => {
						return new SafePlugin({
							view: (editorView: EditorView) => {
								// Do not cleanup the editorViewRef on destroy
								// because some functions may point to a stale
								// reference and this means we will return null.
								// EditorView is assumed to be stable so we do not need to
								// cleanup.
								editorViewRef.current = editorView;
								return {};
							},
						});
					},
				},
			];

			pmPlugins.push({
				name: 'aiDefinitions',
				plugin: (params) => createAIDefinitionsPMPlugin(params, { api, config }),
			});

			return pmPlugins;
		},

		getSharedState(editorState) {
			if (!editorState) {
				return null;
			}
			const pluginState = aiDefinitionsPluginKey.getState(editorState);

			if (!pluginState) {
				return null;
			}

			return {
				enabled: pluginState.enabled,
				autoHighlightEnabled: pluginState.autoHighlightEnabled,
				isReadingAidsPopupOpen: pluginState.isReadingAidsPopupOpen,
				readingAidsPopupKey: pluginState.readingAidsPopupKey,
				readingAidsPopupProps: pluginState.readingAidsPopupProps,
			};
		},

		contentComponent: ({ editorView }) => {
			if (!editorView || isSSR()) {
				return null;
			}

			return (
				<AIDefinitionsErrorBoundary api={api}>
					<AIDefinitions
						api={api}
						editorView={editorView}
						contentId={config.contentId}
						ReadingAidsPopup={config.ReadingAidsPopup}
					/>
				</AIDefinitionsErrorBoundary>
			);
		},

		pluginsOptions: {
			selectionToolbar: (state, intl) => {
				const pluginState = getPluginState(state);
				if (!pluginState.enabled) {
					return;
				}

				const items: SelectionToolbarGroup['items'] = [];

				// only show AI define button in consumption mode
				if (api && selectionHasText(state)) {
					const aiDefinitionItem =
						api?.editorViewMode.sharedState.currentState()?.mode === 'view'
							? getAIDefineButton({ intl, state, config, api })
							: null;
					aiDefinitionItem && items.push(aiDefinitionItem);
				}

				if (items.length) {
					return {
						rank: editorExperiment('platform_editor_controls', 'variant1') ? 10 : -10,
						isToolbarAbove: true,
						items,
					};
				}
			},
		},

		commands: {
			// this will update the plugin state and trigger the usePluginHook to rerun
			updateState: updateState({ editorViewRef }),
		},
		actions: {
			openReadingAidsPopup: () => {
				const readingAidsPopupProps = getReadingAidsSelectionProps(editorViewRef.current?.state);
				if (
					editorViewRef.current &&
					editorViewRef.current.state &&
					editorViewRef.current.dispatch &&
					readingAidsPopupProps
				) {
					openReadingAidsPopup({
						api,
						readingAidsPopupProps,
						source: 'define-button-click',
					})(editorViewRef.current.state, editorViewRef.current.dispatch);
				}
			},
			selectionExceedsMaxWordCount: () => {
				const state = editorViewRef.current?.state;
				if (!state) {
					return false;
				}
				return selectionExceedsMaxWordCount(state, config);
			},
		},
	};
};
